import React, { useState, useEffect } from "react";
import { Card, Badge, ProgressBar, Spinner, Alert } from "react-bootstrap";
import { format } from "date-fns";
import api from "../api/api";
import PropTypes from "prop-types";

const ViewExerciseFeedback = ({ exerciseId, selectedDate, hasFeedbackHistory }) => {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await api.get(
          `/feedback/?exercise=${exerciseId}&date_completed=${selectedDate}`
        );

        // Assuming the most recent feedback is first in the array
        const latestFeedback = response.data[0];
        setFeedback(latestFeedback);
      } catch (err) {
        setError("Failed to load feedback");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [exerciseId, selectedDate]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }
  ViewExerciseFeedback.propTypes = {
    exerciseId: PropTypes.number.isRequired,
    selectedDate: PropTypes.string.isRequired,
    hasFeedbackHistory: PropTypes.bool,
  };

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!feedback) {
    if (!hasFeedbackHistory) {
      return (
        <Alert variant="info">No feedback has been submitted for this activity.</Alert>
      );
    }

    return (
      <Alert variant="info">
        No feedback was submitted for this date. Previous feedback is available.
      </Alert>
    );
  }


  return (
    <div className="mt-4 border-top pt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h6 className="nav-text mb-0">Client Feedback</h6>
        <small className="text-muted">
          Submitted{" "}
          {format(new Date(feedback.date_completed), "MMM d, yyyy 'at' h:mm a")}
        </small>
      </div>

      {/* Effort Rating */}
      <div className="mb-4">
        <label className="text-muted mb-2">Effort Level</label>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted">0</span>
          <ProgressBar
            now={feedback.effort_rating * 10}
            className="w-100"
            style={{ height: "2rem" }}
          />
          <span className="text-muted">10</span>
        </div>
        <div className="text-center mt-2">
          <Badge bg="custom-pink">{feedback.effort_rating}</Badge>
        </div>
      </div>

      {/* Feeling/Comments */}
      {feedback.feeling && (
        <div className="mb-4">
          <label className="text-muted mb-2">How it felt</label>
          <Card className="bg-light">
            <Card.Body>
              <p className="mb-0 text-dark preserve-whitespace">
                {feedback.feeling}{" "}
              </p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Media Upload */}
      <div className="mb-3">
        {!feedback.image_url && !feedback.video_url && (
          <h6>No feedback images or videos for this activity.</h6>
        )}
        {feedback.image_url && (
          <div className="mb-3">
            <img
              src={feedback.image_url}
              alt={`${feedback.exercise.name} feedback`}
              className="img-fluid rounded"
              style={{ maxHeight: "300px", objectFit: "contain" }}
            />
          </div>
        )}
        {feedback.video_url && (
          <div className="mb-3">
            <h6 className="text-muted">Activity Video</h6>
            <video
              controls
              className="w-100 rounded"
              style={{ maxHeight: "300px" }}
            >
              <source src={feedback.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewExerciseFeedback;
